import { z } from 'zod';
import { DistanceUnit, CardioType } from '../types/programs.js';
import { ActivityType } from '../types/activityTemplates.js';
// MongoDB ObjectId validation
const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/, 'Invalid ObjectId format');
// Enums
const ActivityTypeSchema = z.nativeEnum(ActivityType);
export const DistanceUnitSchema = z.nativeEnum(DistanceUnit);
export const CardioTypeSchema = z.nativeEnum(CardioType);
// Set schema - single set with reps and percentage
export const SetSchema = z.object({
    reps: z.number().int().min(1, 'Reps must be at least 1').max(100, 'Reps cannot exceed 100'),
    percentageOfMax: z.number().min(0, 'Percentage must be at least 0').max(200, 'Percentage cannot exceed 200'),
    templateRepMaxId: objectId.optional()
});
// Base activity schema (without refinements)
const BaseActivitySchema = z.object({
    _id: objectId.optional(),
    activityTemplateId: objectId,
    type: ActivityTypeSchema,
    order: z.number().int().min(0),
    sets: z.array(SetSchema).min(0).max(20, 'Cannot exceed 20 sets').optional(),
    cardioType: CardioTypeSchema.optional(),
    time: z.number().int().min(0).optional(), // in minutes
    distance: z.number().min(0).optional(),
    distanceUnit: DistanceUnitSchema.optional(),
    repetitions: z.number().int().min(1).max(10000).optional(),
    templateSubMaxId: objectId.optional(), // Reference to benchmark sub-max for cardio
    percentageOfMax: z.number().min(0, 'Percentage must be at least 0').max(200, 'Percentage cannot exceed 200').optional(),
    notes: z.string().trim().max(500, 'Notes must be less than 500 characters').optional()
});
// Activity schema with refinements
export const ActivitySchema = BaseActivitySchema.refine((data) => {
    // Only validate cardio activities
    if (data.type !== ActivityType.CARDIO)
        return true;
    // Benchmark mode validation
    const isBenchmarkMode = data.templateSubMaxId && data.percentageOfMax;
    if (isBenchmarkMode)
        return true;
    // Static mode requires cardioType
    if (!data.cardioType)
        return false;
    // Validate required fields per type
    if (data.cardioType === CardioType.TIME)
        return !!data.time;
    if (data.cardioType === CardioType.DISTANCE)
        return !!(data.distance && data.distanceUnit);
    if (data.cardioType === CardioType.REPETITIONS)
        return !!data.repetitions;
    return true;
}, {
    message: 'Cardio activities must have valid fields for their cardioType'
});
// Day schema
export const DaySchema = z.object({
    _id: objectId.optional(),
    name: z.string().min(1).max(100).trim(),
    order: z.number().int().min(0),
    activities: z.array(ActivitySchema).default([])
});
// ActivityGroupTarget schema
export const ActivityGroupTargetSchema = z.object({
    activityGroupId: objectId,
    targetPercentage: z.number().min(0).max(100)
});
// Week schema
export const WeekSchema = z.object({
    _id: objectId.optional(),
    name: z.string().min(1).max(100).trim(),
    order: z.number().int().min(0),
    activityGroupTargets: z.array(ActivityGroupTargetSchema).default([]),
    days: z.array(DaySchema).default([])
});
// Block schema
export const BlockSchema = z.object({
    _id: objectId.optional(),
    name: z.string().min(1).max(100).trim(),
    order: z.number().int().min(0),
    activityGroupTargets: z.array(ActivityGroupTargetSchema).default([]),
    weeks: z.array(WeekSchema).default([])
});
// Program creation schema
export const CreateProgramSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters').trim(),
    description: z.string().max(500, 'Description must be less than 500 characters').trim().optional(),
    gymId: objectId
});
// Program update schema
export const UpdateProgramSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters').trim().optional(),
    description: z.string().max(500, 'Description must be less than 500 characters').trim().optional(),
    isActive: z.boolean().optional(),
    blocks: z.array(BlockSchema).optional()
});
// Program list params schema
export const ProgramListParamsSchema = z.object({
    gymId: objectId.optional(),
    isActive: z.coerce.boolean().optional(),
    createdBy: objectId.optional(),
    search: z.string().optional(),
    page: z.coerce.number().min(1, 'Page must be at least 1').default(1),
    limit: z.coerce.number().min(1, 'Limit must be at least 1').max(100, 'Limit cannot exceed 100').default(10)
});
// Program ID schema
export const ProgramIdSchema = z.object({
    id: objectId
});
// Block management schemas
export const CreateBlockSchema = BlockSchema;
export const UpdateBlockSchema = BlockSchema.partial();
// Week management schemas
export const CreateWeekSchema = WeekSchema;
export const UpdateWeekSchema = WeekSchema.partial();
// Day management schemas
export const CreateDaySchema = DaySchema;
export const UpdateDaySchema = DaySchema.partial();
// Activity management schemas
export const CreateActivitySchema = ActivitySchema;
export const UpdateActivitySchema = BaseActivitySchema.partial();
// Program progress schemas
export const JumpToWeekSchema = z.object({
    blockIndex: z.number().int().min(0, 'Block index must be at least 0'),
    weekIndex: z.number().int().min(0, 'Week index must be at least 0')
});
//# sourceMappingURL=programs.js.map